import React, { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';

import { trackViewedProduct } from 'utils/klaviyo';
import { useOnCheckoutClick } from 'utils/checkout-link';
import useLocalCurrency from 'utils/useLocalCurrency';
import { useTextFontOverrides, useHeadingFontOverrides } from 'utils/font-override';
import { buildCssVar } from 'utils/style-override';
import { convertPrice } from 'utils/price';
import * as rudder from 'utils/rudderstack';

import MultiVariantBundleItemDesktop from './components/BundleItemDesktop';
import MultiVariantBundleItemMobile from './components/BundleItemMobile';

import type {
  ComponentsMultiVariantBundleStyle,
  ComponentsMultiVariantBundleOfferContainer,
  ComponentsMultiVariantBundleOfferContainerSalesOffer as MvbSalesOffer,
} from 'queries/graphql';
import { SalesFunnelData, useFunnelData } from 'utils/funnel-data-context';

const MediaQuery = dynamic(() => import('react-responsive'), {
  ssr: false,
});

export default function MultiVariantBundleItem({
  heading,
  ctaText,
  salesOffers: offers,
  style,
}: ComponentsMultiVariantBundleOfferContainer & {
  style: ComponentsMultiVariantBundleStyle;
}) {
  const {
    backgroundColor,
    borderColor,
    textColor,
    compareAtColor,
    headingColor,
    headingBackgroundColor,
    buttonTextColor,
    buttonBackgroundColor,
    buttonHoverColor,
    buttonHoverTextColor,
    buttonBorderColor,
    buttonHoverBorderColor,
    headingFont,
    textFont,
    starColor,
    starEmptyColor,
  } = style || {};

  const { symbol: currencySymbol, rate: localFxRate } = useLocalCurrency();

  const [shouldShowActivityIndicator, setShouldShowActivityIndicator] = useState(false);

  // TODO: add payment method and should track options to useOnCheckoutClick
  const getCheckoutClickHandler = useOnCheckoutClick({
    setShouldShowActivityIndicator,
    shouldTrackSelectItem: true,
  });

  const [selectedVariant, setSelectedVariant] = useState<MvbSalesOffer>(offers[0]);

  const variantOffers = selectedVariant?.salesPageOffer;
  const selectedOffer = variantOffers?.offer;
  const selectedVariantName = selectedVariant?.variantName;

  useEffect(() => {
    offers.forEach(offer => trackViewedProduct(offer?.salesPageOffer?.offer));
  }, [offers]);

  const headingFontOverrides = useHeadingFontOverrides(headingFont);
  const textFontOverrides = useTextFontOverrides(textFont);

  const buttonBorderColorOverride = buttonBorderColor || buttonBackgroundColor;
  const buttonHoverBorderColorOverride = buttonHoverBorderColor || buttonHoverColor;
  const cssVarsText = `
  .root {
    ${buildCssVar('--heading-color', headingColor, 'var(--primary-color)')}
    ${buildCssVar('--heading-background-color', headingBackgroundColor, 'var(--secondary-color)')}
    ${buildCssVar('--background-color', backgroundColor, 'var(--default-background-color)')}
    ${buildCssVar('--border-color', borderColor, 'var(--primary-color)')}
    ${buildCssVar('--text-color', textColor, 'var(--text-color)')}
    ${buildCssVar('--compare-at-color', compareAtColor, 'var(--color-negative)')}
    ${buildCssVar('--button-text-color', buttonTextColor, 'var(--text-color-light)')}
    ${buildCssVar('--button-background-color', buttonBackgroundColor, 'var(--primary-color)')}
    ${buildCssVar('--button-hover-color', buttonHoverColor, 'var(--primary-color)')}
    ${buildCssVar('--button-hover-text-color', buttonHoverTextColor, 'var(--text-color-light)')}
    ${buildCssVar('--button-border-color', buttonBorderColorOverride, 'var(--primary-color)')}
    ${buildCssVar('--saving-at-color', undefined, 'var(--color-positive)')}
    ${buildCssVar(
      '--button-hover-border-color',
      buttonHoverBorderColorOverride,
      'var(--primary-color)'
    )}
    ${headingFontOverrides ?? ''}
    ${textFontOverrides ?? ''}
  }
`;
  const ref = useRef<HTMLDivElement>(null);
  const [tracked, setTracked] = useState(false);

  const { offersConfig } = useFunnelData<SalesFunnelData>();

  useEffect(() => {
    // Only set up the observer if we haven't tracked this item yet
    if (tracked) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !tracked) {
          const offerConfig = offersConfig.find(_offer => _offer.id === selectedOffer?.hashedId);

          if (offerConfig) {
            rudder.onPromotionViewed(offerConfig, rudder.PAGE_TYPES.SALES_PAGE);
          }

          setTracked(true);
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }

      observer.disconnect();
    };
  }, [tracked, offersConfig, selectedOffer]);

  function handleVariantChange(e: any) {
    const variant = offers.find(offer => offer.variantName === e.target.value);
    const offerConfig = offersConfig.find(
      _offer => _offer.id === variant?.salesPageOffer.offer?.hashedId
    );

    if (offerConfig) {
      rudder.onPromotionClicked(offerConfig, rudder.PAGE_TYPES.SALES_PAGE);
    }

    setSelectedVariant(variant);
  }

  return (
    <>
      <div ref={ref}></div>
      <MediaQuery maxWidth={980}>
        <MultiVariantBundleItemMobile
          ctaText={ctaText}
          getCheckoutClickHandler={getCheckoutClickHandler}
          selectedVariantName={selectedVariantName}
          shouldShowActivityIndicator={shouldShowActivityIndicator}
          offers={offers}
          image={selectedVariant?.image}
          cssVarsText={cssVarsText}
          compareAtPrice={convertPrice(selectedOffer?.compareAtPrice, localFxRate)}
          price={convertPrice(selectedOffer?.price, localFxRate)}
          description={selectedVariant?.text}
          heading={heading}
          // previewImages={previewImages}
          offerId={selectedOffer?.hashedId}
          offerName={selectedOffer?.name}
          onVariantChange={handleVariantChange}
          variantName={selectedVariant?.name}
          rating={selectedVariant?.rating}
          starColor={starColor}
          starEmptyColor={starEmptyColor}
          currencySymbol={currencySymbol}
        />
      </MediaQuery>
      <MediaQuery minWidth={981}>
        <MultiVariantBundleItemDesktop
          ctaText={ctaText}
          getCheckoutClickHandler={getCheckoutClickHandler}
          selectedVariantName={selectedVariantName}
          shouldShowActivityIndicator={shouldShowActivityIndicator}
          image={selectedVariant?.image}
          cssVarsText={cssVarsText}
          compareAtPrice={convertPrice(selectedOffer?.compareAtPrice, localFxRate)}
          price={convertPrice(selectedOffer?.price, localFxRate)}
          description={selectedVariant?.text}
          heading={heading}
          // previewImages={previewImages}
          offers={offers}
          offerId={selectedOffer?.hashedId}
          offerName={selectedOffer?.name}
          onVariantChange={handleVariantChange}
          variantName={selectedVariant?.name}
          currencySymbol={currencySymbol}
        />
      </MediaQuery>
    </>
  );
}
